/** Nav: Keystone's sidebar. The brand, the palette's door; then one column of groups, packed from the top like the reference's: the jobs (People is a link, with its main views as a tree behind the chevron), the queues with their open counts straight under them, and Support. The column scrolls only when the whole of it overflows, so the jobs never leave view; the free space falls above the account card. The shell renders it twice (column and drawer), so everything it shows comes in as props. */
import { useEffect, useRef, useState, type MouseEvent } from "react";
import { useLocation } from "react-router-dom";
import type { User } from "../../api/client";
import { peopleHref } from "../data/queues";
import type { Queues } from "../data/useQueues";
import { cx, Mark, MenuItem, Sidebar, SidebarAccount, SidebarBody, SidebarBrand, SidebarCommand, SidebarGroup, SidebarItem, SidebarLink, SidebarNav, SidebarSubLink, SidebarTree, Skeleton, useSidebarSlot } from "../kit";
import { DEFAULT_VIEW, staticTitle } from "../screens/people/views";
import { canSee, JOBS, PEOPLE_TREE, QUEUE_ICON } from "./jobs";
import { href } from "./links";

interface NavProps {
  queues: Queues;
  /** Undefined while loading; null when nobody is signed in. */
  me: User | null | undefined;
  /** The tenant role's capabilities; undefined while loading (gated jobs stay hidden until known). */
  capabilities: readonly string[] | undefined;
  onPalette: () => void;
  onFeedback: () => void;
  onHelp: () => void;
  onSignOut: () => void;
}

const QUEUE_KEYS = Object.keys(QUEUE_ICON);

const initials = (name: string) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

/** Whether this viewer left the People tree open. */
const TREE_KEY = "keystone:people-tree";

/** The viewer's own choice ("1" open, "0" closed), or null until they press the chevron. */
function recalled(): boolean | null {
  try {
    const kept = localStorage.getItem(TREE_KEY);
    return kept === null ? null : kept === "1";
  } catch {
    return null;
  }
}

/** A row's place while its data loads, at the row's own height, so nothing below moves when it lands. */
function RowSkeleton() {
  return (
    <div className="flex h-9 items-center px-2.5">
      <Skeleton className="h-3 w-32" />
    </div>
  );
}

/** True while the column has more below its fold: only then does its foot fade, so a column that fits is never dimmed. */
function useMoreBelow() {
  const box = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  useEffect(() => {
    const nav = box.current?.firstElementChild;
    // jsdom has no layout to observe.
    if (!nav || typeof ResizeObserver === "undefined") return;
    const check = () => setMore(nav.scrollTop + nav.clientHeight < nav.scrollHeight - 1);
    const observer = new ResizeObserver(check);
    observer.observe(nav);
    for (const child of Array.from(nav.children)) observer.observe(child);
    nav.addEventListener("scroll", check, { passive: true });
    return () => {
      observer.disconnect();
      nav.removeEventListener("scroll", check);
    };
  }, []);
  return [box, more] as const;
}

export function Nav({ queues, me, capabilities, onPalette, onFeedback, onHelp, onSignOut }: NavProps) {
  const { pathname, search } = useLocation();
  const view = pathname === "/people" ? new URLSearchParams(search).get("view") || DEFAULT_VIEW : null;
  // Closed until the viewer opens it, so the column fits the screen with Support in view; only its own button moves it, and the choice is remembered, so a navigation never shifts a row under the pointer.
  const [choice, setChoice] = useState(recalled);
  const treeOpen = choice ?? false;
  const toggleTree = () => {
    setChoice(!treeOpen);
    try {
      localStorage.setItem(TREE_KEY, treeOpen ? "0" : "1");
    } catch {
      // Storage is blocked: the tree still opens, it is only not remembered.
    }
  };
  // One row is lit: the queue's, else the view's leaf when the tree shows it, else People itself.
  const queueView = queues.list.some((row) => row.view === view);
  const inTree = (PEOPLE_TREE as readonly (string | null)[]).includes(view);
  const settings = JOBS.find((job) => job.to === "/settings");
  const jobs = JOBS.filter((job) => job !== settings && canSee(job, capabilities));
  const gated = JOBS.filter((job) => job.capability);
  const name = me ? me.full_name || me.username : "";
  const { dismiss } = useSidebarSlot();
  const [scroller, moreBelow] = useMoreBelow();
  // After a navigation the lit row is brought into view, if the column has to scroll to show it (the column only: the page and the drawer never move).
  useEffect(() => {
    const nav = scroller.current?.firstElementChild;
    const lit = nav?.querySelector("[aria-current=page]");
    if (!nav || !lit) return;
    const [box, row] = [nav.getBoundingClientRect(), lit.getBoundingClientRect()];
    if (row.bottom > box.bottom) nav.scrollTop += row.bottom - box.bottom + 4;
    else if (row.top < box.top) nav.scrollTop -= box.top - row.top + 4;
  }, [scroller, pathname, search]);
  /** Choosing a destination in the drawer closes it; in the column, `dismiss` would collapse the sidebar, so it is left alone. */
  const closeDrawer = (event: MouseEvent) => {
    const target = event.target as Element;
    if (target.closest("a[href]") && target.closest("[aria-modal=true]")) dismiss();
  };

  return (
    <Sidebar>
      {/* Listens for clicks on the links inside it; the links are the controls. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="contents" onClick={closeDrawer}>
        <SidebarBrand name="Keystone" logo={<Mark />} href={href("/")} />
        <SidebarBody>
          <SidebarCommand label="Search" onOpen={onPalette} />
          {/* One column of groups at the reference's group gap; it takes the room that is left, scrolls only when all of it overflows, and its foot fades only while there is more below it. */}
          <div ref={scroller} className={cx("flex min-h-0 flex-1 flex-col", moreBelow && "mask-b-from-[calc(100%-20px)]")}>
            <SidebarNav>
              <SidebarGroup label="Main Navigation">
                {jobs.map((job) =>
                  job.to === "/people" ? (
                    <SidebarTree key={job.to} label={job.label} icon={job.icon} href={href(job.to)} active={pathname === job.to && !queueView && !(treeOpen && inTree)} open={treeOpen} onToggle={toggleTree} toggleLabel="People views">
                      {PEOPLE_TREE.map((id) => (
                        <SidebarSubLink key={id} label={staticTitle(id)} href={href(peopleHref(id))} active={treeOpen && view === id} />
                      ))}
                    </SidebarTree>
                  ) : (
                    <SidebarLink key={job.to} label={job.label} icon={job.icon} href={href(job.to)} active={job.to === "/" ? pathname === "/" : pathname.startsWith(job.to)} />
                  ),
                )}
                {/* A gated job holds its row until the role's capabilities are known. */}
                {capabilities === undefined && gated.map((job) => <RowSkeleton key={job.to} />)}
              </SidebarGroup>
              {/* The day's work, straight under the jobs. Tight, like Support. */}
              <SidebarGroup label="Needs you" tight>
                {queues.loading ? (
                  QUEUE_KEYS.map((key) => <RowSkeleton key={key} />)
                ) : queues.failed ? (
                  <p className="flex h-9 items-center px-2.5 text-meta leading-none text-muted-foreground">Did not load</p>
                ) : (
                  queues.list.map((row) => <SidebarLink key={row.key} label={row.reason} icon={QUEUE_ICON[row.key]} href={href(peopleHref(row.view))} count={row.count} unit="open" active={view === row.view} />)
                )}
              </SidebarGroup>
              <SidebarGroup label="Support" tight>
                <SidebarItem label="Feedback" icon="comment" muted onSelect={onFeedback} />
                <SidebarItem label="Help" icon="headset" muted onSelect={onHelp} />
                {settings && <SidebarLink label={settings.label} icon={settings.icon} href={href(settings.to)} muted active={pathname.startsWith(settings.to)} />}
              </SidebarGroup>
            </SidebarNav>
          </div>
          {me ? (
            <SidebarAccount name={name} detail={me.email || me.role} initials={initials(name)} label="Account">
              <MenuItem asChild>
                <a href={href("/settings?open=users")}>Your account</a>
              </MenuItem>
              <MenuItem onSelect={onSignOut}>Sign out</MenuItem>
            </SidebarAccount>
          ) : (
            me === undefined && <Skeleton className="h-[58px] w-full shrink-0 rounded-xl" />
          )}
        </SidebarBody>
      </div>
    </Sidebar>
  );
}
